import { cacheProvider, cacheAdapter } from "contexts/Cache";
import keycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import { debug } from "common/Debug";
import { httpClientAuthorizer } from "@sia/style-guide";
import { EncryptionProvider } from "common/crypto";
import { interpolateURL } from "../../interpolateUrl";
import { formatSurveyData } from "./utils/surveyDataUtils";
import { createSurveyService } from "./services/surveyService";
import { createCompanyService } from "./services/companyService";
import { createOverheadsService } from "./services/overheadsService";
import { createEmployeeService } from "./services/employeeService";
import { createCollectionDataService } from "./services/collectionDataService";
import { createSurveyCryptoTransitionService } from "./services/surveyCryptoTransitionService";
import { surveyApiClient } from "./surveyApiClient";

const API_URL = process.env.REACT_APP_SURVEY_API_URL;
const TRUSTEE_NAME = process.env.REACT_APP_BENCHMARKING_SURVEY_TRUSTEE_NAME;
const COLLECTION_DATA_CACHE_KEY = "collection-data-model";
const logError = (endpoint, error, controller) => {
  console.error(`/${endpoint} Error =====`, error?.response?.data || error);
  if (controller) controller.abort();
};

export default function createSurveyAdapter(httpClient) {
  const apiClient = surveyApiClient({ httpClient, httpClientAuthorizer, keycloakIDPAdapter });
  const surveyService = createSurveyService({ apiClient, apiUrl: API_URL, interpolateURL, logError, debug });
  const companyService = createCompanyService({
    apiClient,
    apiUrl: API_URL,
    interpolateURL,
    logError,
    debug,
    trusteeName: TRUSTEE_NAME,
  });
  const collectionDataService = createCollectionDataService({ apiClient, apiUrl: API_URL, interpolateURL, logError });
  const overheadsService = createOverheadsService({ apiClient, apiUrl: API_URL, interpolateURL, logError, debug });
  const employeeService = createEmployeeService({ apiClient, apiUrl: API_URL, interpolateURL, logError, debug });
  const cryptoTransitionService = createSurveyCryptoTransitionService({ apiClient, apiUrl: API_URL, interpolateURL });
  const clearCollectionDataCache = () => cacheAdapter().clearCache(COLLECTION_DATA_CACHE_KEY);
  const captureSession = () => ({ ...EncryptionProvider.requireSnapshot(), engine: EncryptionProvider.getCapabilities() });
  const assertSession = (session) => EncryptionProvider.assertRevision(session.revision);
  const withSessionMutation = (operation, expectedRevision = null) => {
    const requestedRevision = expectedRevision ?? EncryptionProvider.requireSnapshot().revision;
    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(requestedRevision);
      const session = captureSession();
      const result = await operation(session);
      assertSession(session);
      return result;
    });
  };

  return {
    async fetchSurvey(lang) {
      return cacheProvider(
        `survey/${lang || "de"}`,
        async () => {
          const res = await surveyService.getSurvey();
          return res.ok ? res.data : {};
        },
        !debug.useDebugMode
      );
    },

    async fetchData(translate, onSuccess) {
      const session = captureSession();
     const rawData = await collectionDataService.fetchData({ companyHash: session.companyHash, companyService, translate });
      return EncryptionProvider.runExclusive(async () => {
        assertSession(session);
        const formattedData = await formatSurveyData(rawData, translate, session.engine);
        assertSession(session);
        if (typeof onSuccess === "function") onSuccess(formattedData);
        return formattedData;
      });
    },

    fetchEmployeeFunctionsForNoga(nogaId) {
      return collectionDataService.fetchEmployeeFunctionsForNoga({ nogaId });
    },

    getCompanyHash() {
      return EncryptionProvider.getSnapshot().companyHash;
    },

    assertCryptoSessionRevision(expectedRevision) {
      EncryptionProvider.assertRevision(expectedRevision);
      return true;
    },

    async fetchNewTemplateAndDefineCompanyHash({ nogaCodeObj, survey, translate, cantonObj, companyId, officeSizeObj }) {
      return withSessionMutation(async (session) => {
        clearCollectionDataCache();
        const res = await cacheProvider(
          COLLECTION_DATA_CACHE_KEY,
          () =>
            companyService.buildNewTemplateModel({
              companyHash: session.companyHash,
              requestHash: session.companyHash,
              nogaCodeObj,
              cantonObj,
              officeSizeObj,
              survey,
              translate,
              companyId,
            }),
          !debug.useDebugMode
        );
        if (!res?.ok || !res.data) return false;
        debug.enhancedLog(debug.useDebugMode, "Compiled DataModel", "blue", res.data);
        return res.data;
      });
    },

    async updateCompanyDetails(stateSlice, companyDetailId, companyId, companyInput, payloadObj, selectedNoga, selectedOfficeSize, onSuccess) {
      return withSessionMutation(async (session) => {
        const res = await companyService.patchCompanyOrDetail({
          stateSlice,
          companyDetailId,
          companyId,
          companyInput,
          payloadObj,
          selectedNoga,
          selectedOfficeSize,
          requestHash: session.companyHash,
        });
        if (res.ok && typeof onSuccess === "function") onSuccess(res.data);
        return res;
      });
    },

    deleteEmployee(id, expectedCryptoRevision = null) {
      return withSessionMutation((session) => employeeService.deleteEmployee({ id, hash: session.companyHash }), expectedCryptoRevision);
    },

    deleteEmployeeInBulk(ids, expectedCryptoRevision = null) {
      return withSessionMutation((session) => employeeService.deleteEmployeeInBulk({ ids, hash: session.companyHash }), expectedCryptoRevision);
    },

    async upsertEmployee({ employee, companyId, companyInputId, dropdownOptions, employees, expectedCryptoRevision = null }) {
      const res = await withSessionMutation(
        (session) =>
          employeeService.upsertEmployee({
            employee,
            companyId,
            companyInputId,
            dropdownOptions,
            employees,
            hash: session.companyHash,
            engine: session.engine,
          }),
        expectedCryptoRevision
      );
      return { ...res, isSuccess: res.ok };
    },

    async updateOverheadCosts(overheads, companyInputId, translate) {
      const res = await withSessionMutation((session) =>
        overheadsService.patchOverheads({ overheads, companyInputId, translate, hash: session.companyHash })
      );
      return res.data;
    },

    async submitDataForReview(surveyId, companyInput, companyId, clientReply) {
      const res = await withSessionMutation((session) => {
        clearCollectionDataCache();
        return companyService.submitCompanyInput({
          surveyId,
          companyInput,
          companyId,
          clientReply,
          requestHash: session.companyHash,
        });
      });
      return res.ok;
    },

    async reopenSubmittedSurvey(surveyId, companyInput, companyId) {
      const res = await withSessionMutation((session) => {
        clearCollectionDataCache();
        return companyService.reopenCompanyInput({
          surveyId,
          companyInput,
          companyId,
          requestHash: session.companyHash,
        });
      });
      return res.ok;
    },

    clearCollectionDataCache,

    async getCryptoMigrationEmployees(companyHash) {
      const result = await employeeService.listEmployees({ hash: companyHash });
      if (!result?.ok) throw result?.error || new Error("Employees could not be loaded before changing the master key");
      return result.data || [];
    },

    async applyCryptoTransition(plan) {
      const response = await cryptoTransitionService.apply(plan);
      clearCollectionDataCache();
      return response;
    },
  };
}
